/**
 * Cross-route runtime narrowing helpers for `Article.metadata` shapes.
 *
 * Why this module exists
 * ======================
 *
 * `Article.metadata` is `Record<string, unknown>` at the port boundary
 * (see `ContentReaderPort.ts`'s JSDoc). Each bespoke route narrows the
 * shape it expects via per-field type guards before consuming the
 * structured fields. Guard primitives that are not specific to one
 * route (`isOutboundLink` for any route that surfaces the proto's
 * "Lue lisää …" footer link; `isReadonlyArrayOf` for any route whose
 * metadata carries an array of structured entries) live here, which
 * eliminates byte-identical copies and keeps the per-route guard
 * modules focused on their route-specific shapes.
 *
 * Scope policy
 * ============
 *
 * Per-route guards stay co-located with their route. Route-agnostic
 * helpers live here:
 *
 *   - `isOutboundLink` was duplicated verbatim across
 *     `VapaaAjanTapaturmavakuutusRoute.tsx`,
 *     `OikeusturvavakuutusRoute.tsx`, and
 *     `lakikaveriInfoGuards.ts`.
 *   - `isReadonlyArrayOf<T>` is generic over the element guard; it
 *     currently has one consumer (`lakikaveriInfoGuards.ts`).
 *
 * What this module does NOT own
 * =============================
 *
 *   - Per-route metadata shapes (e.g.
 *     `isVapaaAjanTapaturmaMetadata`,
 *     `isLakikaveriInfoMetadata`). Those guards walk fields specific
 *     to one route and stay co-located with the route.
 *   - Per-route inner-shape guards (e.g. `isAccordionSection`,
 *     `isLakikaveriStep`). Those guards apply to
 *     a single metadata shape and stay co-located with the route that
 *     owns the shape.
 */

import type { OutboundLink } from '@omaplus/shared-types/content-metadata';

/**
 * Narrow an unknown candidate to `OutboundLink` (the
 * `{ href: string; labelFi: string }` shape used by every
 * "Lue lisää …" outbound footer link in Phase A content metadata).
 *
 * Takes `value: unknown` (not `Record<string, unknown>`) because TS
 * type-predicate functions require their narrowed type to be
 * assignable to the parameter type, and the metadata interfaces use
 * `readonly` properties that are NOT assignable to a
 * `Record<string, unknown>` index signature. Accepting `unknown` is
 * the canonical type-guard parameter shape.
 */
export function isOutboundLink(value: unknown): value is OutboundLink {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return typeof record['href'] === 'string' && typeof record['labelFi'] === 'string';
}

/**
 * Validate every entry of an unknown candidate against `predicate`.
 * Returns `true` when the candidate is an array AND every entry
 * satisfies `predicate`; `false` otherwise.
 *
 * Generic over the narrowed element type `T` so callers can compose
 * with any element-level guard:
 *
 *   if (!isReadonlyArrayOf(record['steps'], isLakikaveriStep)) { ... }
 *
 * Takes `value: unknown` for the same reason as `isOutboundLink`
 * above (TS type-predicate parameter typing).
 */
export function isReadonlyArrayOf<T>(
  value: unknown,
  predicate: (item: unknown) => item is T,
): value is ReadonlyArray<T> {
  if (!Array.isArray(value)) {
    return false;
  }
  for (const item of value) {
    if (!predicate(item)) {
      return false;
    }
  }
  return true;
}
